<script setup lang="ts">
import { createWidget } from '@faslet/widget'
import type { MagentoConfigurableProduct } from '~layers/app/types/magento'

interface Props {
  product: MagentoConfigurableProduct
  esProduct?: any
}

const props = defineProps<Props>()

const emit = defineEmits<{ 'faslet-visible': [value: boolean] }>()

const { t } = useI18n()
const { addToCart } = useCart()

const recommendedSizeText = ref('')
const isFasletButtonVisible = ref(false)
const hasRecommendedSize = computed(() => !!recommendedSizeText.value)

const sizeAttributeCode = computed(
  () => props.product.configurable_options?.[0]?.attribute_code ?? 'size',
)

const handleAddToCart = async (variantId: string): Promise<void> => {
  const isConfigurable = props.product.__typename === 'ConfigurableProduct'
  const variantSku = props.product.variants.find(
    variant => String(variant.product.id) === String(variantId),
  )?.product.sku

  await addToCart(
    [
      {
        ...(isConfigurable && { parent_sku: props.product.sku }),
        data: {
          sku: variantSku || variantId,
          quantity: 1,
        },
      },
    ],
    false,
  ).then(() => {
    return Promise.resolve()
  })
}

const handleButtonShow = () => {
  isFasletButtonVisible.value = true
  emit('faslet-visible', true)
}

const handleButtonHidden = () => {
  isFasletButtonVisible.value = false
  emit('faslet-visible', false)
}

const onWidgetResult = ({ label }: { label: string }) => {
  recommendedSizeText.value = t('faslet.recommended_size', { size: `<strong>${label}</strong>` })
}

const openFasletWidget = () => {
  widget.openWidget()
}

const productImageUrl = props.esProduct.image || 'null'
const productName = props.product.name
const productId = String(props.product.id)
const productBrand = props.esProduct?.brand || 'null'
const productColor = props.esProduct.color?.label || 'null'

const widget = createWidget('deleye')
  .withBrand(productBrand)
  .withProductId(productId)
  .withProductImage(productImageUrl)
  .withProductName(productName)
  .withAddToCart(handleAddToCart)
  .withOnButtonShow(handleButtonShow)
  .withOnButtonHidden(handleButtonHidden)
  .withOnResult(onWidgetResult)

onMounted(() => {
  widget.addColor(productColor, productColor)

  props.product.variants.forEach((variant) => {
    widget.addVariant(
      String(variant.product.id),
      variant.attributes.find(a => a.code === sizeAttributeCode.value)?.label ?? '',
      variant.product.stock_status === 'IN_STOCK',
      variant.product.sku,
      productColor,
      variant.product.price_range?.minimum_price?.final_price?.value,
      productImageUrl,
    )
  })

  widget.injectScriptTag()
  widget.addToDom('#faslet-container')
})

onBeforeUnmount(() => {
  widget.removeFromDom()
  isFasletButtonVisible.value = false
  emit('faslet-visible', false)
})
</script>

<template>
  <div
    v-show="isFasletButtonVisible"
    class="faslet__container"
  >
    <div id="faslet-container" />
    <button
      type="button"
      class="faslet__button button button--link"
      @click.prevent="openFasletWidget()"
    >
      <UiRichtext :html="hasRecommendedSize ? recommendedSizeText : $t('faslet.default_size')" />
    </button>
  </div>
</template>

<style>
  @import url("~layers/app/components/Thirdparty/Faslet/Sizing.css");
</style>
